<script setup lang="ts">
import { computed } from 'vue';
import { ShoppingCart, Trash2 } from 'lucide-vue-next';
import { formatCurrency } from '@shared/utils/currency';
import { useCartStore } from '../../stores/cart-store';

defineProps<{
  title?: string;
  checkoutLabel?: string;
  compact?: boolean;
}>();

const emit = defineEmits<{
  (event: 'checkout'): void;
  (event: 'edit-item', item: any, index: number): void;
}>();

const cartStore = useCartStore();
const totalItems = computed(() => cartStore.totalItems);
const totalPrice = computed(() => cartStore.totalPrice);

const updateQuantity = (index: number, delta: number) => {
  cartStore.updateItemQuantity(index, delta);
};
</script>

<template>
  <aside class="cart-rail" :class="{ 'cart-rail--compact': compact }" data-client-critical="cart">
    <header class="cart-rail__header">
      <span class="cart-rail__icon" aria-hidden="true"><ShoppingCart :size="20" /></span>
      <div>
        <h2>{{ title || 'Carrito' }}</h2>
        <p class="cart-rail__count" aria-live="polite" aria-atomic="true">{{ totalItems }} productos</p>
      </div>
    </header>

    <div v-if="cartStore.items.length === 0" class="cart-rail__empty" role="status" aria-live="polite">
      Agrega productos del menú para continuar.
    </div>

    <ul v-else class="cart-rail__items">
      <li v-for="(item, index) in cartStore.items" :key="`${item.id}-${index}`" class="cart-rail__item">
        <div class="cart-rail__item-main">
          <strong>{{ item.name }}</strong>
          <span>{{ formatCurrency((item.price + item.extrasTotal) * item.quantity) }}</span>
        </div>
        <div class="cart-rail__controls">
          <button type="button" class="cart-rail__edit" :aria-label="`Editar ${item.name}`" title="Editar opciones" @click="emit('edit-item', item, index)">Editar</button>
          <button type="button" :aria-label="`Reducir cantidad de ${item.name}`" :disabled="item.quantity <= 1" @click="updateQuantity(index, -1)">-</button>
          <span :aria-label="`Cantidad: ${item.quantity}`">{{ item.quantity }}</span>
          <button type="button" :aria-label="`Aumentar cantidad de ${item.name}`" @click="updateQuantity(index, 1)">+</button>
          <button type="button" class="cart-rail__remove" :aria-label="`Eliminar ${item.name}`" title="Eliminar" @click="cartStore.removeItem(index)">
            <Trash2 :size="16" />
          </button>
        </div>
      </li>
    </ul>

    <footer class="cart-rail__footer">
      <div class="cart-rail__total">
        <span>Total</span>
        <strong>{{ formatCurrency(totalPrice) }}</strong>
      </div>
      <button
        type="button"
        class="cart-rail__checkout"
        :disabled="cartStore.isEmpty"
        @click="emit('checkout')"
      >
        {{ checkoutLabel || 'Confirmar pedido' }}
      </button>
    </footer>
  </aside>
</template>

<style scoped>
.cart-rail {
  --cart-accent: #ea580c;
  --cart-accent-strong: #ea580c;
  position: static !important;
  top: auto !important;
  bottom: auto !important;
  max-height: none !important;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  overflow: visible !important;
  background: #fff;
  border: 1px solid #d8dee8;
  border-radius: 8px;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.12);
  margin: 0 !important;
  transform: none !important;
}

.cart-rail--compact {
  position: relative;
  top: 0;
  max-height: none;
  border-width: 2px;
}

.cart-rail__header,
.cart-rail__item-main,
.cart-rail__controls,
.cart-rail__total {
  display: flex;
  align-items: center;
}

.cart-rail__header {
  gap: 0.75rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid #e5e7eb;
}

.cart-rail__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 8px;
  color: #fff;
  background: var(--cart-accent);
}

.cart-rail h2,
.cart-rail p {
  margin: 0;
}

.cart-rail h2 {
  color: #172033;
  font-size: 1rem;
}

.cart-rail p,
.cart-rail__empty {
  color: #64748b;
  font-size: 0.88rem;
}

.cart-rail__items {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  min-height: 0;
  padding: 0;
  margin: 0;
  overflow: auto;
  list-style: none;
}

.cart-rail__item {
  display: grid;
  gap: 0.55rem;
  padding: 0.8rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.cart-rail__item-main,
.cart-rail__total {
  justify-content: space-between;
  gap: 0.75rem;
}

.cart-rail__item-main strong {
  min-width: 0;
  color: #172033;
  font-size: 0.92rem;
}

.cart-rail__item-main span {
  flex-shrink: 0;
  color: var(--cart-accent-strong);
  font-weight: 800;
}

.cart-rail__controls {
  gap: 0.5rem;
}

.cart-rail__controls button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #172033;
  font-weight: 800;
  cursor: pointer;
}

.cart-rail__controls button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.cart-rail__edit {
  padding: 4px 10px !important;
  width: auto !important;
  min-width: 44px !important;
  min-height: 44px !important;
  font-size: 0.75rem !important;
  color: #2563eb !important;
  border-color: #cbd5e1 !important;
}

.cart-rail__remove {
  margin-left: auto;
  color: #b91c1c !important;
  min-width: 44px !important;
  min-height: 44px !important;
}

.cart-rail__footer {
  display: grid;
  gap: 0.9rem;
  padding-top: 0.8rem;
  border-top: 1px solid #e5e7eb;
}

.cart-rail__total strong {
  color: #172033;
  font-size: 1.35rem;
}

.cart-rail__checkout {
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: var(--cart-accent);
  font-weight: 850;
  cursor: pointer;
}

.cart-rail__checkout:hover:not(:disabled) {
  background: var(--cart-accent-strong);
}

.cart-rail__checkout:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}
</style>
